import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BadgePercent, TrendingDown, Rocket, Layers, ShieldCheck, Database, Star, UserCheck, Gift, MonitorCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { awsServices } from "@/data/awsServices";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AwsCalculator } from "@/components/AwsCalculator";
import { LeadForm } from "@/components/LeadForm";
import WorldHero from "@/components/WorldHero";
import { Reveal, FadeStagger, FadeItem } from "@/components/Motion";

export const ICONS = { BadgePercent, TrendingDown, Rocket, Layers, ShieldCheck, Database };

const WHY_ICONS = [BadgePercent, UserCheck, Gift, Rocket, MonitorCheck, TrendingDown];

const AwsHub = () => {
  const { t, lang } = useLang();

  return (
    <div data-testid="aws-hub" className="bg-[#FAFAFD] text-[#353535] min-h-screen">
      <Navbar accent="#ff764a" />

      <WorldHero
        testid="aws-hero"
        accent="#ff764a"
        kicker={t.aws.kicker}
        l1={t.aws.heroL1}
        l2={t.aws.heroL2}
        sub={t.aws.heroSub}
        cta1={{ label: t.aws.heroCta1, to: "/contact?unit=aws&service=aws-reselling" }}
        cta2={{ label: t.aws.heroCta2, to: "#aws-calculadora" }}
      >
        <div data-testid="aws-hero-visual" className="w-full max-w-md rounded-2xl border border-black/10 bg-white p-7 shadow-[0_24px_60px_rgba(53,53,53,0.07)]">
          <div className="flex items-center justify-between">
            <img src="/aws-logo.svg" alt="AWS" className="h-7 w-auto" />
            <span className="rounded-full bg-[#ff764a]/10 px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#ff764a]">{t.aws.heroBadge}</span>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-2">
            {["EC2", "S3", "RDS", "Aurora", "Redshift", "DynamoDB"].map((svc) => (
              <span key={svc} className="rounded-lg border border-black/10 bg-[#FAFAFD] px-3 py-2.5 text-center font-jbmono text-[10px] uppercase tracking-widest text-[#737373]">
                {svc}
              </span>
            ))}
          </div>
          <p className="mt-5 text-xs font-jakarta text-[#999999] leading-relaxed">{t.aws.heroVisualNote}</p>
        </div>
      </WorldHero>

      <section id="aws-calculadora" className="px-5 sm:px-8 pb-24">
        <Reveal className="mx-auto max-w-[1400px]">
          <AwsCalculator />
        </Reveal>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-24 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#ff764a] mb-4">{t.aws.whyKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight mb-14">{t.aws.whyTitle}</h2>
          </Reveal>
          <FadeStagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" delay={0.1}>
            {t.aws.whyItems.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <FadeItem key={i}>
                  <div data-testid={`aws-why-${i}`} className="h-full rounded-2xl border border-black/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(42,42,42,0.08)]">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#ff764a]/10 text-[#ff764a]">
                      <Icon size={20} />
                    </span>
                    <h3 className="mt-5 font-cabinet text-lg font-bold">{w.t}</h3>
                    <p className="mt-2 text-sm text-[#999999] font-jakarta leading-relaxed">{w.d}</p>
                  </div>
                </FadeItem>
              );
            })}
          </FadeStagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#ff764a] mb-4">{t.aws.servicesKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl">{t.aws.servicesTitle}</h2>
          </Reveal>
          <FadeStagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" delay={0.1}>
            {awsServices.map((s) => {
              const Icon = ICONS[s.icon];
              const c = s[lang];
              return (
                <FadeItem key={s.slug}>
                  <Link
                    to={`/aws/services/${s.slug}`}
                    data-testid={`aws-service-card-${s.slug}`}
                    className={`group relative flex h-full flex-col rounded-2xl border p-7 transition-all duration-500 hover:-translate-y-1.5 bg-white ${
                      s.popular
                        ? "border-[#ff764a] shadow-[0_20px_60px_rgba(255,153,0,0.12)]"
                        : "border-black/10 hover:border-black/25 hover:shadow-[0_20px_50px_rgba(30,30,56,0.08)]"
                    }`}
                  >
                    {s.popular && (
                      <span data-testid="aws-popular-badge" className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-[#ff764a] px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#0B1120]">
                        <Star size={10} /> {t.common.mostPopular}
                      </span>
                    )}
                    <div className="flex items-start justify-between">
                      <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${s.popular ? "bg-[#ff764a] text-white" : "bg-[#353535]/[0.06] text-[#ff764a]"}`}>
                        <Icon size={22} />
                      </span>
                      <ArrowUpRight size={18} className="text-slate-400 transition-all duration-300 group-hover:text-[#ff764a] group-hover:rotate-45" />
                    </div>
                    <h3 className="mt-6 font-playfair text-xl">{c.name}</h3>
                    <p className="mt-2 text-sm text-[#999999] font-jakarta leading-relaxed flex-1">{c.tagline}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-jbmono text-[11px] uppercase tracking-widest text-[#ff764a]">
                      {t.common.viewService} <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </FadeItem>
              );
            })}
          </FadeStagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{t.aws.ctaTitle}</h2>
            <p className="mt-5 text-[#999999] font-jakarta max-w-md">{t.aws.ctaBody}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <LeadForm defaultUnit="aws" accent="#ff764a" />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AwsHub;
